import { generatePath } from "react-router-dom";

import { AdminViewTabName } from "~/modules/admin/types/adminViewTypes.ts";
import type {
	ValidInvoiceDetailsNavigatedFromOrigin,
} from "~/modules/billing/components/InvoiceDetailsView/invoiceDetailsUtils.ts";
import { ProjectDetailsTabName } from "~/modules/project/types/projectViewTypes.ts";
import type {
	ValidQuotaDetailsNavigatedFromOrigin,
} from "~/modules/quota/components/QuotaDetailsView/quotaDetailsUtils.ts";

export interface AppRoutes {
	path: string;
	displayName: string;
}

export const CHANGELOG_PATH = "/changelog";
export const CLIENTS_INDEX_PATH = "/clients";
export const CLIENT_DETAILS_PATH = CLIENTS_INDEX_PATH + "/:clientId";
export const CLIENT_CONTACT_PATH = "/contacts/:contactId";
export const CENTRAL_INVOICE_RECIPIENT_DETAILS_PATH = CLIENT_DETAILS_PATH + "/central-invoice-recipient/:centralInvoiceRecipientId";
export const CENTRAL_INVOICE_RECIPIENTS_INDEX_PATH = "/central-invoice-recipients";
export const QUOTAS_INDEX_PATH = "/quotas";
export const COMPANY_DETAILS_PATH = "/company/:companyId";
// ADR 0007: a quota is owned by a company (the customer), so its detail route scopes by companyId.
export const QUOTA_DETAILS_PATH = COMPANY_DETAILS_PATH + "/quotas/:quotaId";

// The project origin also names its project, because the consumers of a quota can span more than one project.
export const QUOTA_DETAILS_PROJECT_ID_PARAM_NAME = "projectId";
// The invoice origin also names its invoice, because an invoice-backed quota can hold more than one invoice.
export const QUOTA_DETAILS_INVOICE_ID_PARAM_NAME = "invoiceId";

export const QUOTA_DETAILS_PATH_WITH_ORIGIN = (companyId: string,
	quotaId: string,
	navigatedFrom: ValidQuotaDetailsNavigatedFromOrigin,
	{ invoiceId, projectId }: { invoiceId?: string; projectId?: string } = {}) => {
	const searchParams = new URLSearchParams({ from: navigatedFrom });
	if (projectId) searchParams.set(QUOTA_DETAILS_PROJECT_ID_PARAM_NAME, projectId);
	if (invoiceId) searchParams.set(QUOTA_DETAILS_INVOICE_ID_PARAM_NAME, invoiceId);
	return generatePath(QUOTA_DETAILS_PATH, { companyId, quotaId }) + `?${searchParams.toString()}`;
};

const HR_BASE_PATH = "/hr";

export const HR_STAFF_MEMBERS_INDEX_PATH = HR_BASE_PATH + "/staff-members";

export const HR_STAFF_MONTHLY_REPORT_PATH = HR_BASE_PATH + "/monthly-report";

export const HR_STAFF_BENEFITS_REPORT_PATH = HR_BASE_PATH + "/benefits-report";

export const HR_STATISTICS_PATH = HR_BASE_PATH + "/statistics";

export const HR_TEAM_OVERVIEW_PATH = HR_BASE_PATH + "/team";

export const INVOICES_INDEX_PATH = "/invoices";
export const INVOICES_PENDING_PATH = "/pending-invoices";
export const INVOICE_DETAILS_PATH = INVOICES_INDEX_PATH + "/:invoiceId";

// The revenue origin also names its project, because a quota invoice of another project also serves this one.
export const INVOICE_DETAILS_PROJECT_ID_PARAM_NAME = "projectId";

export const INVOICE_DETAILS_PATH_WITH_ORIGIN = (invoiceId: string,
	navigatedFrom: ValidInvoiceDetailsNavigatedFromOrigin,
	{ projectId }: { projectId?: string } = {}) => {
	const searchParams = new URLSearchParams({ from: navigatedFrom });
	if (projectId) searchParams.set(INVOICE_DETAILS_PROJECT_ID_PARAM_NAME, projectId);
	return generatePath(INVOICE_DETAILS_PATH, { invoiceId }) + `?${searchParams.toString()}`;
};

export const STAFF_MEMBER_BASE_PATH = "/staff-members/:staffMemberId";

export const STAFF_MEMBER_ABSENCES_PATH = STAFF_MEMBER_BASE_PATH + "/absences";

export const STAFF_MEMBER_VACATION_LEDGER_PATH = STAFF_MEMBER_BASE_PATH + "/vacation-ledger";

export const STAFF_MEMBER_WORKING_SCHEDULES_PATH = STAFF_MEMBER_BASE_PATH + "/working-schedules";

const HR_STAFF_MEMBER_DETAILS_PATH = HR_STAFF_MEMBERS_INDEX_PATH + "/:staffMemberId";
export const HR_STAFF_MEMBER_DETAILS_PATH_WITH_TAB_ID = HR_STAFF_MEMBER_DETAILS_PATH + "/:tabId";

export const PROJECT_DESCRIPTIONS_INDEX_PATH = "descriptions";

const PROJECT_DETAILS_BASE_PATH = "/projects/:projectId";

export const PROJECT_DETAILS_PATH_WITH_TAB_ID = PROJECT_DETAILS_BASE_PATH + "/:tabId";

export const PROJECT_DETAILS_PATH_WITH_DEFAULT_TAB_ID = PROJECT_DETAILS_BASE_PATH + `/${ProjectDetailsTabName.Overview}`;

const ADMIN_BASE_PATH = "/admin";

export const ADMIN_PATH_WITH_TAB_ID = ADMIN_BASE_PATH + "/:tabId";

export const ADMIN_PATH_WITH_DEFAULT_TAB_ID = generatePath(ADMIN_PATH_WITH_TAB_ID, { tabId: AdminViewTabName.Users });

export const TIME_TRACKINGS_PATH = "/user/:userId/time-trackings";

const REPORTS_BASE_PATH = "/reports";

export const REPORTS_PATH_WITH_TAB_ID = REPORTS_BASE_PATH + "/:tabId";



export const appRoutes = {
	absenceOverview: () => ({ path: "/absence-overview", displayName: "Übersicht Abwesenheiten" }),
	base: () => ({ path: "/", displayName: "Home" }),
	client: (displayName?: string) => ({ path: "/clients/:clientId", displayName }),
	clients: () => ({ path: "/clients", displayName: "Kunden" }),
	contact: (displayName?: string) => ({ path: "/contacts/:contactId", displayName }),
	contacts: () => ({ path: "/contacts", displayName: "Kontakte" }),
	login: () => ({ path: "/login", displayName: "Login" }),
	logout: () => ({ path: "/logout", displayName: "Logout" }),
	monthlyBillingReport: () => ({ path: "/billing/monthly-report/", displayName: "Abrechnung" }),
	passwordResetRequest: () => ({ path: "/request-password-reset", displayName: "Passwort zurücksetzen" }),
	passwordReset: () => ({ path: "/reset-password/:token", displayName: "Passwort zurücksetzen" }),
	projects: () => ({ path: "/projects", displayName: "Projekte" }),
	userProfile: () => ({ path: "/profile", displayName: "Profil" }),
};
